/**
 * Slide-in sidepanel som viser full hjelpe-artikkel med markdown-rendering.
 * Lukkes med X, Esc eller klikk på backdrop.
 */
import { useEffect, useState } from 'react';
import { X, Loader2, AlertCircle, Lightbulb } from 'lucide-react';
import { createPortal } from 'react-dom';
import { getArticle, logView, categoryLabel, type HelpArticle, type HelpLanguage } from '../../lib/help';
import MarkdownRenderer from './MarkdownRenderer';

interface Props {
  slug: string;
  language?: HelpLanguage;
  isOpen: boolean;
  onClose: () => void;
  route?: string;
}

export default function HelpPanel({ slug, language = 'nb', isOpen, onClose, route }: Props) {
  const [article, setArticle] = useState<HelpArticle | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    getArticle(slug, language)
      .then(a => {
        setArticle(a);
        logView(slug, 'panel', route, language);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug, language, isOpen, route]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex" role="dialog" aria-modal="true" aria-label="Hjelp">
      <div
        className="absolute inset-0 bg-kf-gray-900/40 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <div className="relative ml-auto h-full w-full max-w-xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
        <header className="flex items-start justify-between gap-4 px-6 py-4 border-b border-kf-gray-200 bg-kf-blue-50">
          <div className="flex items-start gap-2 min-w-0">
            <Lightbulb className="w-5 h-5 text-kf-orange-500 shrink-0 mt-0.5" aria-hidden="true" />
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-kf-gray-500 font-semibold">
                Hjelp {article?.category && `· ${categoryLabel(article.category)}`}
              </div>
              <h2 className="text-lg font-semibold text-kf-gray-900 leading-snug truncate">
                {article?.title || (loading ? 'Laster...' : 'Hjelp')}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1 rounded hover:bg-kf-blue-100 text-kf-gray-600"
            aria-label="Lukk hjelp"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-kf-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              Henter artikkel...
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Kunne ikke laste artikkel</div>
                <div className="text-xs mt-0.5">{error}</div>
                <div className="text-xs mt-1 text-red-700">Slug: <code>{slug}</code></div>
              </div>
            </div>
          )}
          {article && (
            <>
              {article.summary && (
                <div className="mb-4 text-sm text-kf-gray-700 italic border-l-4 border-kf-orange-300 pl-3">
                  {article.summary}
                </div>
              )}
              <MarkdownRenderer content={article.body_markdown} />
              <div className="mt-6 pt-4 border-t border-kf-gray-100 text-xs text-kf-gray-500">
                Versjon {article.version} · Oppdatert {new Date(article.updated_at).toLocaleDateString('nb-NO')}
                {article.tags?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {article.tags.map(t => (
                      <span key={t} className="px-1.5 py-0.5 bg-kf-gray-100 rounded text-[10px]">
                        #{t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
